import { Fragment } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollHorizontal } from "@/components/ScrollHorizontal";
import { SecaoCard, SegmentedControl } from "@/components/padroes";
import { formatCents } from "@/lib/format";
import { cn } from "@/lib/utils";
import { mesCurto, porcento, type Bloco, type DashboardRH, type Grupo } from "@/data/dashboard";
import { competenciaAnterior, setaVariacao } from "./variacao";
import { ChevronRight, Columns3 } from "lucide-react";

export type Quebra = "tomador" | "departamento" | "composicao";

const DICAS: Record<Quebra, string> = {
  tomador: "Clique num tomador para abrir os departamentos dele.",
  departamento: "Todos os tomadores somados.",
  composicao: "Cada parte do custo, separada entre PJ e CLT.",
};

const num = "text-right tabular-nums";

/** Células de um grupo (tomador, departamento ou total), na ordem das colunas. */
const CelulasGrupo = ({ g, detalhar }: { g: Grupo; detalhar: boolean }) => {
  const vs = setaVariacao(g.custo, g.custoAnterior);
  return (
    <>
      <TableCell className={num}>
        <span className="font-medium">{g.headcount.total}</span>
        <span className="block text-[11px] font-normal text-muted-foreground">
          {g.headcount.pj} PJ · {g.headcount.clt} CLT
        </span>
      </TableCell>
      {detalhar && (
        <>
          <TableCell className={num}>{formatCents(g.salario + g.complemento)}</TableCell>
          <TableCell className={num}>{formatCents(g.variaveis + g.encargos)}</TableCell>
          <TableCell className={num}>{formatCents(g.beneficios)}</TableCell>
          <TableCell className={num}>{formatCents(g.bonificacao)}</TableCell>
        </>
      )}
      <TableCell className={cn(num, "font-semibold")}>{formatCents(g.custo)}</TableCell>
      <TableCell className={num}>
        <span className="flex flex-col items-end gap-1">
          {porcento(g.participacao)}
          <span className="flex h-1 w-full max-w-[120px] rounded-sm bg-border-soft">
            <span className="h-full rounded-sm bg-primary" style={{ width: `${Math.min(100, g.participacao * 100)}%` }} />
          </span>
        </span>
      </TableCell>
      <TableCell className={num}>{g.headcount.total ? formatCents(g.custoMedio) : "—"}</TableCell>
      <TableCell className={num}>{vs ?? "—"}</TableCell>
    </>
  );
};

/** Tabela PJ × CLT: as partes do custo e, em cinza, os valores informativos. */
const TabelaComposicao = ({ dados, nomeBonificacao }: { dados: DashboardRH; nomeBonificacao: string }) => {
  const { total, porVinculo } = dados;
  const linha = (rotulo: string, f: (b: Bloco) => number, className?: string) => (
    <TableRow key={rotulo} className={className}>
      <TableCell>{rotulo}</TableCell>
      <TableCell className={num}>{formatCents(f(porVinculo.pj))}</TableCell>
      <TableCell className={num}>{formatCents(f(porVinculo.clt))}</TableCell>
      <TableCell className={num}>{formatCents(f(total))}</TableCell>
    </TableRow>
  );
  return (
    <Table className="min-w-[700px]">
      <TableHeader>
        <TableRow>
          <TableHead />
          <TableHead className="text-right">PJ</TableHead>
          <TableHead className="text-right">CLT</TableHead>
          <TableHead className="text-right">Total</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {linha("Salário fixo / base", (b) => b.salario)}
        {linha("Complemento de salário", (b) => b.complemento)}
        {linha("Variáveis (comissões, horas, férias, 13º, ajustes)", (b) => b.variaveis)}
        {linha("Encargos (FGTS)", (b) => b.encargos)}
        {linha("Benefícios", (b) => b.beneficios)}
        {linha(nomeBonificacao, (b) => b.bonificacao)}
        {linha("Custo total", (b) => b.custo, "bg-muted-soft font-semibold hover:bg-muted-soft")}
        {/* Abaixo do custo total: informativos, fora da soma */}
        {linha("Plano de saúde — titular (após franquia)", (b) => b.saude.titular, "text-muted-foreground")}
        {linha("Plano de saúde — dependentes", (b) => b.saude.dependentes, "text-muted-foreground")}
        {linha("Franquia absorvida pela empresa", (b) => b.saude.franquiaEmpresa, "text-muted-foreground")}
        {linha("INSS + IRRF retidos (CLT)", (b) => b.retencoes, "text-muted-foreground")}
        {linha("Empréstimos e descontos abatidos", (b) => b.emprestimos, "text-muted-foreground")}
        {linha("Líquido pago na folha", (b) => b.liquido, "text-muted-foreground")}
      </TableBody>
    </Table>
  );
};

/**
 * "Onde está o custo": por tomador (expandindo os departamentos), por
 * departamento ou PJ × CLT, num card só.
 */
export const CardOndeEstaOCusto = ({
  dados,
  nomeBonificacao,
  quebra,
  onQuebra,
  detalhar,
  onDetalhar,
  abertos,
  onAlternar,
}: {
  dados: DashboardRH;
  nomeBonificacao: string;
  quebra: Quebra;
  onQuebra: (q: Quebra) => void;
  detalhar: boolean;
  onDetalhar: (d: boolean) => void;
  /** Chaves dos tomadores expandidos. */
  abertos: Set<string>;
  onAlternar: (chave: string) => void;
}) => {
  const porTomador = quebra === "tomador";
  const totalGrupo: Grupo = {
    ...dados.total,
    chave: "total",
    nome: "Total",
    participacao: 1,
    custoAnterior: dados.anterior.custo,
  };

  return (
    <SecaoCard
      titulo="Onde está o custo"
      descricao={DICAS[quebra]}
      semPadding
      acoes={
        <>
          <SegmentedControl
            aria-label="Quebra do custo"
            value={quebra}
            onChange={onQuebra}
            options={[
              { value: "tomador", label: "Por tomador" },
              { value: "departamento", label: "Por departamento" },
              { value: "composicao", label: "PJ × CLT" },
            ]}
          />
          {quebra !== "composicao" && (
            <Button
              variant="outline"
              size="sm"
              className="h-[34px] text-xs"
              onClick={() => onDetalhar(!detalhar)}
              aria-pressed={detalhar}
            >
              <Columns3 className="!size-3.5" />
              {detalhar ? "Menos colunas" : "Detalhar colunas"}
            </Button>
          )}
        </>
      }
      rodape={
        quebra === "composicao" ? (
          <p className="text-xs text-muted-foreground">
            Abaixo do custo total, em cinza: valores informativos. O sistema conhece o que é repassado aos
            colaboradores e a franquia que a empresa absorve, não a fatura do plano de saúde.
          </p>
        ) : undefined
      }
    >
      <ScrollHorizontal>
        {quebra === "composicao" ? (
          <TabelaComposicao dados={dados} nomeBonificacao={nomeBonificacao} />
        ) : (
          <Table className={detalhar ? "min-w-[1320px]" : "min-w-[760px]"}>
            <TableHeader>
              <TableRow>
                <TableHead>{porTomador ? "Tomador" : "Departamento"}</TableHead>
                <TableHead className="text-right">Pessoas</TableHead>
                {detalhar && (
                  <>
                    <TableHead className="text-right">Salários</TableHead>
                    <TableHead className="text-right">Variáveis + FGTS</TableHead>
                    <TableHead className="text-right">Benefícios</TableHead>
                    <TableHead className="text-right">{nomeBonificacao}</TableHead>
                  </>
                )}
                <TableHead className="text-right">Custo total</TableHead>
                <TableHead className="text-right">% do total</TableHead>
                <TableHead className="text-right">Custo médio</TableHead>
                <TableHead className="whitespace-nowrap text-right">
                  vs {mesCurto(competenciaAnterior(dados.competencia))}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {porTomador
                ? dados.porTomador.map((t) => {
                    const aberto = abertos.has(t.chave);
                    return (
                      <Fragment key={t.chave}>
                        <TableRow
                          className="cursor-pointer"
                          onClick={() => onAlternar(t.chave)}
                          aria-expanded={aberto}
                        >
                          <TableCell className="font-medium">
                            <span className="flex items-center gap-2">
                              <ChevronRight
                                className={cn(
                                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                                  aberto && "rotate-90",
                                )}
                              />
                              <span className="truncate">{t.nome}</span>
                            </span>
                          </TableCell>
                          <CelulasGrupo g={t} detalhar={detalhar} />
                        </TableRow>
                        {aberto &&
                          t.departamentos.map((d) => (
                            <TableRow key={`${t.chave}-${d.chave}`} className="bg-muted-soft/50 text-foreground/80">
                              <TableCell className="pl-14">{d.nome}</TableCell>
                              <CelulasGrupo g={{ ...d, participacao: d.participacao * t.participacao }} detalhar={detalhar} />
                            </TableRow>
                          ))}
                      </Fragment>
                    );
                  })
                : dados.porDepartamento.map((d) => (
                    <TableRow key={d.chave}>
                      <TableCell className="font-medium">{d.nome}</TableCell>
                      <CelulasGrupo g={d} detalhar={detalhar} />
                    </TableRow>
                  ))}
              <TableRow className="bg-muted-soft font-semibold hover:bg-muted-soft">
                <TableCell className={cn(porTomador && "pl-10")}>Total</TableCell>
                <CelulasGrupo g={totalGrupo} detalhar={detalhar} />
              </TableRow>
            </TableBody>
          </Table>
        )}
      </ScrollHorizontal>
    </SecaoCard>
  );
};
